import { QueryClient } from "@tanstack/react-query";

/**
 * Optimized Query Client Configuration for Scaling
 * 
 * Key optimizations:
 * - Stale time: Data stays fresh for 5 minutes to reduce API calls
 * - GC time: Cached data persists for 30 minutes for fast navigation
 * - Retry logic: Exponential backoff with max 3 retries
 * - Refetch on mount: Only if data is stale
 * - Background refetch: Keeps data fresh without blocking UI
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is fresh for 5 minutes - reduces unnecessary refetches
      staleTime: 5 * 60 * 1000,
      
      // Keep unused data in cache for 30 minutes
      gcTime: 30 * 60 * 1000,
      
      // Only refetch on mount if data is stale
      refetchOnMount: "always",
      
      // Refetch when window regains focus (good for real-time data)
      refetchOnWindowFocus: true,
      
      // Don't refetch on reconnect if data is fresh
      refetchOnReconnect: "always",
      
      // Retry failed requests with exponential backoff
      retry: (failureCount, error: unknown) => {
        // Don't retry on 4xx errors (client errors)
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500) return false;
        }
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Keep previous data while fetching new data
      placeholderData: (previousData: unknown) => previousData,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
      retryDelay: 1000,
    },
  },
});

/**
 * Query key factories for consistent caching
 * Using factory pattern prevents key collisions and enables smart invalidation
 */
export const queryKeys = {
  // User data
  profile: (userId: string) => ["profile", userId] as const,
  
  // Resumes
  resumes: {
    all: (userId: string) => ["resumes", userId] as const,
    single: (resumeId: string) => ["resumes", "detail", resumeId] as const,
  },
  
  // CV Requests
  cvRequests: {
    all: (userId: string) => ["cvRequests", userId] as const,
    single: (requestId: string) => ["cvRequests", "detail", requestId] as const,
  },
  
  // Job Applications
  jobs: {
    all: (userId: string) => ["jobs", userId] as const,
    single: (jobId: string) => ["jobs", "detail", jobId] as const,
  },
  
  // Cover Letters
  coverLetters: {
    all: (userId: string) => ["coverLetters", userId] as const,
  },
  
  // Notifications
  notifications: {
    user: (userId: string) => ["notifications", "user", userId] as const,
    admin: () => ["notifications", "admin"] as const,
  },
  
  // Chat/Conversations
  conversations: {
    all: (userId: string) => ["conversations", userId] as const,
    messages: (conversationId: string) => ["conversations", "messages", conversationId] as const,
  },
  
  // Admin data
  admin: {
    users: () => ["admin", "users"] as const,
    resumes: () => ["admin", "resumes"] as const,
    payments: () => ["admin", "payments"] as const,
    stats: () => ["admin", "stats"] as const,
    cvRequests: () => ["admin", "cvRequests"] as const,
  },
  
  // Referrals
  referrals: {
    code: (userId: string) => ["referrals", "code", userId] as const,
    stats: (userId: string) => ["referrals", "stats", userId] as const,
  },
} as const;

/**
 * Invalidation helpers for common operations
 */
export const invalidateQueries = {
  userResumes: (userId: string) => 
    queryClient.invalidateQueries({ queryKey: queryKeys.resumes.all(userId) }),
  
  userCVRequests: (userId: string) =>
    queryClient.invalidateQueries({ queryKey: queryKeys.cvRequests.all(userId) }),
  
  userJobs: (userId: string) =>
    queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all(userId) }),
  
  userNotifications: (userId: string) =>
    queryClient.invalidateQueries({ queryKey: queryKeys.notifications.user(userId) }),
  
  adminData: () => {
    queryClient.invalidateQueries({ queryKey: ["admin"] });
  },
  
  conversationMessages: (conversationId: string) =>
    queryClient.invalidateQueries({ queryKey: queryKeys.conversations.messages(conversationId) }),
};
